// SPDX-License-Identifier: AGPL-3.0-or-later

import * as CosmeticCommands from '@app/features/cosmetics/commands/CosmeticCommands';
import Cosmetics, {type CosmeticType} from '@app/features/cosmetics/state/Cosmetics';
import {isRoomPartType} from '@app/features/cosmetics/utils/RoomOutfitModels';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {
	failureCode,
	failureContext,
	failureMessage,
	failureRetryAfter,
} from '@app/features/platform/utils/ResponseInspection';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import * as ShopCommands from '@app/features/shop/commands/ShopCommands';
import type {ShopCheckoutTarget} from '@app/features/shop/components/modals/ShopCheckoutModal';
import type {ShopEquippedSuccessResult} from '@app/features/shop/components/ShopEquippedSuccess';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {openPaddleCheckout, paddleCheckoutUnavailable, paddleLocale} from '@app/features/shop/utils/PaddleCheckout';
import {BOOST_SKU, packageSku, usernameSku, VISIONARY_SKU} from '@app/features/shop/utils/ShopEntries';
import {PURCHASE_ADDED_ONCE_CONFIRMED_DESCRIPTOR} from '@app/features/shop/utils/ShopMessageDescriptors';
import {chargeMinor} from '@app/features/shop/utils/ShopPriceFormat';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {isDesktop, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import Users from '@app/features/user/state/Users';
import WindowState from '@app/features/window/state/Window';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import type {CosmeticItemResponse} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {
	type RecurringConsent,
	RecurringEligibility,
	type ShopBillingMode,
} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {
	ShopOrderResponse,
	ShopQuoteResponse,
	UsernameCardAvailabilityStatus,
} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';
import {useLingui} from '@lingui/react/macro';
import {type IReactionDisposer, makeAutoObservable, reaction, runInAction} from 'mobx';
import {useEffect, useState} from 'react';

export type ShopCheckoutState =
	| 'quoting'
	| 'ready'
	| 'name_confirm'
	| 'creating'
	| 'payment_ready'
	| 'price_changed'
	| 'contents_changed'
	| 'awaiting'
	| 'approved'
	| 'failed'
	| 'failed_mismatch'
	| 'timeout'
	| 'sales_closed'
	| 'sold_out'
	| 'busy'
	| 'already_owned'
	| 'boost_limit'
	// Renewing Plus was asked for, but the server offers it to nobody here or not to this account.
	| 'recurring_unavailable';

// The client reads only our own order record; the server asks Payriff. A waiting dialog reads
// it after 2, 2, 3, 5, 8, 13 s and then every 15 s, about once every 7 s over ten minutes.
const POLL_DELAYS_MS = [2000, 2000, 3000, 5000, 8000, 13000, 15000];
const POLL_LIMIT_MS = 10 * 60 * 1000;
const EARLY_CHECK_GAP_MS = 2000;

// Why the order refused a username card's name, said as the name check says it.
export type UsernameNameRefusal = Extract<UsernameCardAvailabilityStatus, 'held' | 'taken' | 'not_allowed' | 'invalid'>;
const NAME_REFUSALS: Partial<Record<string, UsernameNameRefusal>> = {
	[APIErrorCodes.USERNAME_ON_HOLD]: 'held',
	[APIErrorCodes.USERNAME_TAKEN]: 'taken',
	[APIErrorCodes.USERNAME_RESERVED]: 'taken',
	[APIErrorCodes.USERNAME_RELEASE_EMBARGO]: 'taken',
	[APIErrorCodes.USERNAME_BLOCKED]: 'not_allowed',
	[APIErrorCodes.USERNAME_INVALID]: 'invalid',
};
// A paid username order that could not give the name: the money waits for a refund by hand, so the dialog asks for
// support with the order number instead of offering a new try.
const NAME_FAILURES = new Set<ShopOrderResponse['failure_reason']>([
	'username_taken',
	'username_not_allowed',
	'username_already_owned',
	'username_delivery_stalled',
]);

export function shopCheckoutSku(target: ShopCheckoutTarget): string {
	switch (target.kind) {
		case 'item':
			return target.sku;
		case 'premium':
			return packageSku(target.months);
		case 'visionary':
			return VISIONARY_SKU;
		case 'bundle':
			return target.sku;
		case 'boost':
			return BOOST_SKU;
		case 'username':
			return usernameSku(target.length);
	}
}

function sameIds(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
	return a.length === b.length && a.every((id, index) => id === b[index]);
}

export class ShopCheckoutFlow {
	state: ShopCheckoutState = 'quoting';
	quote: ShopQuoteResponse | null = null;
	order: ShopOrderResponse | null = null;
	// The server's own words, for a refusal the dialog has no text of its own for.
	failureMessage: string | null = null;
	// A rate limit that lasts longer than a minute: the daily order bucket.
	waitMinutes: number | null = null;
	// How many bought boost slots the account may hold, from a MAX_PURCHASED_BOOST_SLOTS refusal.
	boostLimit: number | null = null;
	// Taken from the catalog when the quote is asked for, so an item taken off sale meanwhile still shows.
	item: CosmeticItemResponse | null = null;
	// A bundle as the catalog showed it; once the order opens, its items are the ones the order froze.
	bundle: {name: string; name_en?: string | null; items: Array<CosmeticItemResponse>} | null = null;
	checking = false;
	// Paddle's checkout is open on this page rather than in a payment tab.
	inPage = false;
	// Plus bought once or renewing; renewing opens only with its own box ticked for the terms on screen.
	billingMode: ShopBillingMode = 'one_time';
	recurringConsent = false;
	// The server refused the terms the buyer agreed to; the new ones are on screen and need the box again.
	recurringQuoteChanged = false;
	// A boost subscription: how many boosts it starts with; null for everything else.
	quantity: number | null = null;
	// One-time boosts (Payriff): how many 30 day slots the one payment buys.
	boostCount = 1;
	private paidInPage = false;
	// A username card: the name being bought, confirmed before the order opens.
	username: string | null = null;
	// The confirmed name is taken free with the Visionary username right instead of bought.
	free = false;
	// The order refused that name; the dialog shows why and checks the name again (nameChecks moves).
	nameRefusal: UsernameNameRefusal | null = null;
	nameChecks = 0;
	private sku = '';
	// A named username or a personal offer: the one name the dialog sells, and the offer's code.
	private fixed: ShopCommands.UsernameQuoteName | null = null;
	// Opened for the Visionary username right only: nothing is priced.
	private freeOnlyMode = false;
	private quoteRequest = 0;
	private pollIndex = 0;
	private lastCheckAt = Number.NEGATIVE_INFINITY;
	private pollTimer: ReturnType<typeof setTimeout> | null = null;
	private limitTimer: ReturnType<typeof setTimeout> | null = null;
	private watchers: Array<IReactionDisposer> = [];
	private disposed = false;

	constructor(
		private readonly kind: ShopCheckoutTarget['kind'],
		private readonly locale: () => string | undefined = () => undefined,
		private readonly refreshBoostSlots?: () => void,
	) {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	// What the success view shows; the premium end and the Visionary number follow USER_UPDATE.
	get success(): ShopEquippedSuccessResult | null {
		if (this.state !== 'approved') return null;
		const user = Users.currentUser;
		if (this.kind === 'premium') return {kind: 'premium', premiumUntil: user?.premiumUntil?.toISOString() ?? null};
		if (this.kind === 'visionary') return {kind: 'visionary', number: user?.premiumLifetimeSequence ?? null};
		if (this.kind === 'bundle') return this.bundle ? {kind: 'bundle', ...this.bundle} : null;
		if (this.kind === 'boost') {
			if (this.quantity !== null) return {kind: 'boost', quantity: this.quantity};
			const bought = this.order?.quantity ?? 1;
			return bought > 1 ? {kind: 'boost', quantity: bought, oneTime: true} : {kind: 'boost'};
		}
		if (this.kind === 'username') return this.username ? {kind: 'username', name: this.username} : null;
		return this.item ? {kind: 'item', item: this.item} : null;
	}

	// What a renewing order agrees to, from the server's quote and the package's free days in the catalog; null until it
	// quotes a renewing Paddle price. Boosts: the catalog's monthly price of one boost, times the quantity.
	get recurringTerms(): RecurringConsent | null {
		if (this.kind === 'boost') {
			const offer = ShopCatalog.boost?.recurring_quote;
			if (this.billingMode !== 'recurring' || this.quantity === null || !offer) return null;
			return {
				accepted: true,
				terms_version: offer.terms_version,
				amount_minor: offer.amount_minor * this.quantity,
				currency: offer.currency,
				interval_months: offer.interval_months,
				quantity: this.quantity,
			};
		}
		const quote = this.quote;
		if (this.billingMode !== 'recurring' || quote?.billing_mode !== 'recurring' || quote.provider !== 'paddle')
			return null;
		if (!quote.interval_months || !quote.terms_version) return null;
		if (quote.currency !== 'USD' && quote.currency !== 'TRY') return null;
		return {
			accepted: true,
			terms_version: quote.terms_version,
			amount_minor: chargeMinor(quote),
			currency: quote.currency,
			interval_months: quote.interval_months,
			trial_days: ShopCatalog.recurringQuotes.get(this.sku)?.trial_days || null,
		};
	}

	setBoostCount(count: number): void {
		if (!this.order) this.boostCount = count;
	}

	setRecurringConsent(consent: boolean): void {
		this.recurringConsent = consent;
		if (consent) this.recurringQuoteChanged = false;
	}

	// What the purchase gives to wear: the item, or the bundle's items.
	private get items(): Array<CosmeticItemResponse> {
		return this.kind === 'bundle' ? (this.bundle?.items ?? []) : this.item ? [this.item] : [];
	}

	// The price is fixed once an order exists, so only a dialog without one asks again. New terms clear the renewal box.
	async quoteFor(
		sku: string,
		billingMode: ShopBillingMode = this.billingMode,
		quantity: number | null = this.quantity,
		fixed: ShopCommands.UsernameQuoteName | null = this.fixed,
	): Promise<void> {
		if (this.order) return;
		this.sku = sku;
		this.fixed = this.kind === 'username' ? fixed : null;
		this.billingMode = this.kind === 'premium' || this.kind === 'boost' ? billingMode : 'one_time';
		this.quantity = this.kind === 'boost' && this.billingMode === 'recurring' ? quantity : null;
		this.recurringConsent = false;
		this.recurringQuoteChanged = false;
		const entry = ShopCatalog.entryForSku(sku);
		if (entry?.kind === 'item') this.item = entry.item;
		if (entry?.kind === 'bundle')
			this.bundle = {name: entry.bundle.name, name_en: entry.bundle.name_en, items: entry.bundle.items};
		this.state = 'quoting';
		this.quote = null;
		this.failureMessage = null;
		this.waitMinutes = null;
		const request = ++this.quoteRequest;
		// A typed name is priced by its own check, then by the confirm step's quote of that name: the card is not quoted.
		if (this.kind === 'username' && this.fixed === null) {
			this.state = 'ready';
			return;
		}
		// Boosts renew at the catalog's price; the order prices them again.
		if (this.quantity !== null) {
			this.state = this.recurringTerms ? 'ready' : 'recurring_unavailable';
			return;
		}
		try {
			const quote =
				this.billingMode === 'recurring'
					? await ShopCommands.fetchQuote(sku, 'recurring')
					: this.fixed
						? await ShopCommands.fetchQuote(sku, undefined, this.fixed)
						: await ShopCommands.fetchQuote(sku);
			runInAction(() => {
				if (request !== this.quoteRequest || this.disposed) return;
				this.quote = quote;
				// Paddle sells one-time boosts one at a time.
				if (quote.provider === 'paddle') this.boostCount = 1;
				this.state = this.billingMode === 'recurring' && !this.recurringTerms ? 'recurring_unavailable' : 'ready';
			});
		} catch (error) {
			runInAction(() => {
				if (request === this.quoteRequest && !this.disposed) this.refuse(error);
			});
		}
	}

	// The Visionary username right: no price is asked, and the name is only ever taken free.
	freeOnly(sku: string): void {
		if (this.order) return;
		this.sku = sku;
		this.freeOnlyMode = true;
		this.quote = null;
		this.state = 'ready';
	}

	// A username card shows the name once more, with its 5 minute hold, before any order opens; the price shown there
	// is the server's quote of that very name.
	async confirmName(username: string): Promise<void> {
		if (!this.mayConfirm(username) || this.freeOnlyMode) return;
		this.username = username;
		this.nameRefusal = null;
		this.free = false;
		// A named username or an offer was quoted as that very name when the dialog opened.
		if (this.fixed !== null && this.quote) {
			this.state = 'name_confirm';
			return;
		}
		this.state = 'quoting';
		const request = ++this.quoteRequest;
		try {
			const quote = await ShopCommands.fetchQuote(this.sku, undefined, {
				username,
				...(this.fixed?.offer !== undefined && {offer: this.fixed.offer}),
			});
			runInAction(() => {
				if (request !== this.quoteRequest || this.disposed) return;
				this.quote = quote;
				this.state = 'name_confirm';
			});
		} catch (error) {
			runInAction(() => {
				if (request === this.quoteRequest && !this.disposed) this.refuse(error);
			});
		}
	}

	// The same confirm step, for a name the Visionary right gives free.
	confirmFree(username: string): void {
		if (!this.mayConfirm(username)) return;
		this.username = username;
		this.nameRefusal = null;
		this.free = true;
		this.state = 'name_confirm';
	}

	private mayConfirm(username: string): boolean {
		return (
			this.kind === 'username' && this.state === 'ready' && (this.fixed === null || this.fixed.username === username)
		);
	}

	changeName(): void {
		if (this.state !== 'name_confirm') return;
		this.free = false;
		this.state = 'ready';
	}

	// The server gives the name at once or after its delivery step; there is no payment page.
	async claimFree(): Promise<void> {
		const username = this.username;
		if (this.state !== 'name_confirm' || !this.free || username === null) return;
		this.state = 'creating';
		try {
			const order = await ShopCommands.claimVisionaryName(username);
			runInAction(() => {
				this.order = order;
				if (this.disposed) return;
				this.beginWaiting();
				this.settle(order);
			});
		} catch (error) {
			runInAction(() => {
				if (!this.disposed) this.refuse(error);
			});
		}
	}

	// The client sends the sku and nothing else (a username card: and the confirmed name); the server prices the order
	// again.
	async start(): Promise<void> {
		if (this.state !== (this.kind === 'username' ? 'name_confirm' : 'ready') || (!this.quote && this.quantity === null))
			return;
		const terms = this.recurringTerms;
		if (this.billingMode === 'recurring' && (!terms || !this.recurringConsent)) return;
		const quote = this.quote;
		const username = this.username;
		// Only Payriff sells several one-time boosts; one boost names no quantity, as before. The order is that many
		// quoted prices.
		const count =
			this.kind === 'boost' && this.billingMode === 'one_time' && quote?.provider !== 'paddle' ? this.boostCount : 1;
		this.state = 'creating';
		try {
			const offer = this.fixed?.offer;
			const order = await ShopCommands.createOrder(this.sku, {
				...(username !== null && {username}),
				...(username !== null && offer !== undefined && {offer}),
				...(terms && {recurringConsent: terms}),
				...(count > 1 && {quantity: count}),
			});
			// The Payriff order is open from here on; kept at once, so nothing after this can lose it.
			runInAction(() => {
				this.order = order;
			});
			const shownIds = this.bundle?.items.map((item) => item.id) ?? [];
			const orderItems = this.bundle ? await this.itemsOf(order.item_ids ?? []) : null;
			runInAction(() => {
				if (this.disposed) return;
				// The catalog on screen may be older than the bundle: the order's list is what gets paid for, so a
				// different one, even in another order, is shown before any payment page opens.
				if (this.bundle && orderItems) {
					this.bundle = {...this.bundle, items: orderItems};
					if (!sameIds(order.item_ids ?? [], shownIds)) {
						this.state = 'contents_changed';
						void ShopCatalog.load(true);
						return;
					}
				}
				// A renewing order is paid only on the very terms agreed to: anything else is never a payment page.
				if (
					terms &&
					(order.billing_mode !== 'recurring' ||
						order.interval_months !== terms.interval_months ||
						order.terms_version !== terms.terms_version ||
						order.quantity !== terms.quantity ||
						order.currency !== terms.currency ||
						chargeMinor(order) !== terms.amount_minor)
				) {
					this.state = 'failed_mismatch';
					return;
				}
				// What the provider takes: a moved AZN rate under an unchanged Paddle USD charge is no change.
				if (
					quote &&
					(order.final_usd_cents !== quote.final_usd_cents * count ||
						order.currency !== quote.currency ||
						chargeMinor(order) !== chargeMinor(quote) * count)
				) {
					this.state = 'price_changed';
					return;
				}
				this.state = 'payment_ready';
			});
		} catch (error) {
			runInAction(() => {
				if (this.disposed) return;
				if (failureCode(error) === APIErrorCodes.SHOP_RECURRING_QUOTE_CHANGED) void this.requoteChanged();
				else this.refuse(error);
			});
		}
	}

	// Runs in the click that confirms the order's price note; the waiting dialog keeps a button to reopen the page.
	// On the web a Paddle order opens its checkout on this page; the desktop app keeps the payment page, as does a page
	// where Paddle.js did not load or Paddle could not open a checkout.
	openPaymentPage(): void {
		const confirming =
			this.state === 'price_changed' || this.state === 'contents_changed' || this.state === 'payment_ready';
		const inPage = this.order?.paddle_checkout;
		if (confirming && inPage && !isDesktop() && !paddleCheckoutUnavailable()) {
			this.beginWaiting();
			this.inPage = true;
			this.paidInPage = false;
			openPaddleCheckout(inPage, paddleLocale(this.locale()), {
				onCompleted: this.paddleCompleted,
				onClosed: this.paddleClosed,
			}).catch(() => runInAction(this.paddleClosed));
			return;
		}
		if (confirming) this.beginWaiting();
		const url = this.order?.payment_url;
		if (url) void openExternalUrl(url);
	}

	private async requoteChanged(): Promise<void> {
		const reload = ShopCatalog.load(true);
		// The boost terms are the catalog's own, so the new ones are read first.
		if (this.quantity !== null) await reload;
		await this.quoteFor(this.sku);
		runInAction(() => {
			if (this.state === 'ready') this.recurringQuoteChanged = true;
		});
	}

	// Back to a fresh quote: a declined or refused attempt keeps nothing.
	retry(): void {
		this.stopWaiting();
		this.order = null;
		this.free = false;
		if (this.freeOnlyMode) this.freeOnly(this.sku);
		else void this.quoteFor(this.sku);
	}

	async checkAgain(): Promise<void> {
		if (this.state === 'timeout') await this.check();
	}

	// The item came another way meanwhile (a code or an admin); putting it on ends the dialog as a success. A room
	// emote is never put on: it is in the emote menu already.
	async equip(): Promise<void> {
		const item = this.item;
		if (this.state !== 'already_owned' || !item || this.checking) return;
		this.checking = true;
		try {
			if (item.type !== 'room_emote') await Cosmetics.equip({[item.type as CosmeticType]: item.id});
			runInAction(() => this.approve());
		} catch {
			// Still owned and not worn; the button can be pressed again.
		} finally {
			runInAction(() => {
				this.checking = false;
			});
		}
	}

	dispose(): void {
		// Closed while waiting: the boost page reads its slots again, so a payment that went through meanwhile shows.
		if (this.state === 'awaiting' && this.kind === 'boost') this.refreshBoostSlots?.();
		this.disposed = true;
		this.stopWaiting();
	}

	// Paddle's word is no proof: it only pulls the next read of the order forward.
	private paddleCompleted(): void {
		this.paidInPage = true;
		this.checkSoon();
	}

	// Closed before paying, or never opened: back to the order, from which the checkout opens again.
	private paddleClosed(): void {
		if (this.paidInPage || this.state !== 'awaiting' || this.disposed) return;
		this.stopWaiting();
		this.inPage = false;
		this.state = 'payment_ready';
	}

	// The order's items in its order. Items the catalog on screen did not have (bundle-only ones the admin added) are
	// read by ID, or, if that read fails, named by the reloaded catalog's bundle. One deleted since is left out, as
	// delivery skips it too; so is one neither read names, which delivery still gives.
	private async itemsOf(ids: ReadonlyArray<string>): Promise<Array<CosmeticItemResponse>> {
		const known = new Map(this.items.map((item) => [item.id, item]));
		const missing = ids.filter((id) => !known.has(id));
		if (missing.length > 0) {
			try {
				for (const item of await CosmeticCommands.fetchItems(missing)) known.set(item.id, item);
			} catch {
				await ShopCatalog.load(true);
				const entry = ShopCatalog.entryForSku(this.sku);
				if (entry?.kind === 'bundle') for (const item of entry.bundle.items) known.set(item.id, item);
			}
		}
		return ids.flatMap((id) => known.get(id) ?? []);
	}

	// A payment tab unless the in-page checkout opens right after.
	private beginWaiting(): void {
		this.state = 'awaiting';
		this.inPage = false;
		this.pollIndex = 0;
		this.schedulePoll();
		this.limitTimer = setTimeout(this.timeOut, POLL_LIMIT_MS);
		const premiumUntilBefore = Users.currentUser?.premiumUntil?.getTime() ?? 0;
		this.watchers = [
			// A USER_UPDATE that shows the purchase: the item is worn, premium runs longer, the account went lifetime, or
			// the account's username is the bought name.
			reaction(
				() => {
					const user = Users.currentUser;
					switch (this.kind) {
						case 'item':
						case 'bundle':
							return this.items.some((item) => Cosmetics.equippedId(item.type as CosmeticType) === item.id);
						case 'premium':
							return (user?.premiumUntil?.getTime() ?? 0) > premiumUntilBefore;
						case 'visionary':
							return user?.premiumType === UserPremiumTypes.LIFETIME;
						case 'boost':
							return false;
						case 'username':
							return this.username !== null && user?.username.toLowerCase() === this.username;
					}
				},
				(shown) => shown && this.checkSoon(),
			),
			// Back from the payment tab.
			reaction(
				() => WindowState.focused && WindowState.visible,
				(back) => back && this.checkSoon(),
			),
		];
	}

	private stopWaiting(): void {
		if (this.pollTimer) clearTimeout(this.pollTimer);
		if (this.limitTimer) clearTimeout(this.limitTimer);
		this.pollTimer = null;
		this.limitTimer = null;
		for (const dispose of this.watchers) dispose();
		this.watchers = [];
	}

	private schedulePoll(delayMs = POLL_DELAYS_MS[Math.min(this.pollIndex++, POLL_DELAYS_MS.length - 1)]): void {
		if (this.pollTimer) clearTimeout(this.pollTimer);
		this.pollTimer = setTimeout(this.poll, delayMs);
	}

	// At once, but never twice within 2 s: a focus return or a USER_UPDATE pulls the next poll forward.
	private checkSoon(): void {
		if (this.checking) return;
		this.schedulePoll(Math.max(0, this.lastCheckAt + EARLY_CHECK_GAP_MS - Date.now()));
	}

	private async poll(): Promise<void> {
		this.pollTimer = null;
		if (this.state !== 'awaiting') return;
		await this.check();
		runInAction(() => {
			if (this.state === 'awaiting' && !this.disposed) this.schedulePoll();
		});
	}

	private async check(): Promise<void> {
		const order = this.order;
		if (!order || this.checking) return;
		this.checking = true;
		this.lastCheckAt = Date.now();
		try {
			const current = await ShopCommands.fetchOrder(order.id);
			runInAction(() => {
				if (!this.disposed) this.settle(current);
			});
		} catch {
			// A failed read waits for the next poll.
		} finally {
			runInAction(() => {
				this.checking = false;
			});
		}
	}

	// Paid but not yet granted keeps waiting: "Equipped" is shown only once the item is on the account.
	private settle(current: ShopOrderResponse): void {
		switch (current.status) {
			case 'granted':
				this.approve();
				// Delivery wears a room character or its parts on the server, and an emote or Plus changes the emotes I
				// may play; a voice room I am in has them only when asked again.
				if (
					this.kind === 'premium' ||
					this.kind === 'visionary' ||
					this.items.some(
						(item) => item.type === 'room_character' || item.type === 'room_emote' || isRoomPartType(item.type),
					)
				) {
					void Cosmetics.refreshWornRoomCharacter();
				}
				return;
			case 'failed':
				this.finish(
					current.failure_reason === 'amount_mismatch' || NAME_FAILURES.has(current.failure_reason)
						? 'failed_mismatch'
						: 'failed',
				);
				return;
			case 'expired':
			case 'refunded':
				this.finish('failed');
				return;
		}
	}

	private approve(): void {
		this.finish('approved');
		// The subscription's dates live in the premium state, which no gateway event carries.
		if (this.billingMode === 'recurring' && this.kind === 'premium')
			void PremiumCommands.refreshPremiumState().catch(() => undefined);
		if (this.kind === 'item' || this.kind === 'bundle') {
			void Cosmetics.load();
			// The effect lives on the profile, which no gateway event updates.
			if (this.items.some((item) => item.type === 'profile_effect')) UserProfileCommands.clearCurrentUserProfiles();
		}
		// The new slot is only in the boost page's slot list; the catalog does not change with it.
		if (this.kind === 'boost') {
			this.refreshBoostSlots?.();
			return;
		}
		// The premium member discount and the Visionary stock change with a purchase.
		void ShopCatalog.load(true);
		// A bought named username leaves its list, and a used Visionary right leaves the catalog's viewer.
		if (this.kind === 'username') void ShopCatalog.loadUsernameProducts(true);
	}

	private timeOut(): void {
		if (this.state === 'awaiting') this.finish('timeout');
	}

	private finish(state: ShopCheckoutState): void {
		this.stopWaiting();
		this.state = state;
	}

	private refuse(error: unknown): void {
		this.stopWaiting();
		const code = failureCode(error);
		const nameRefusal = code === undefined ? undefined : NAME_REFUSALS[code];
		if (this.kind === 'username' && nameRefusal) {
			this.state = 'ready';
			this.free = false;
			this.nameRefusal = nameRefusal;
			this.nameChecks++;
			return;
		}
		// A refused free claim keeps `free`, so the closed view names the Visionary right rather than a sale.
		if (code === APIErrorCodes.SHOP_SALES_CLOSED || code === APIErrorCodes.SHOP_ITEM_UNAVAILABLE) {
			this.state =
				code === APIErrorCodes.SHOP_ITEM_UNAVAILABLE && this.kind === 'visionary' ? 'sold_out' : 'sales_closed';
			void ShopCatalog.load(true);
			return;
		}
		if (code === APIErrorCodes.MAX_PURCHASED_BOOST_SLOTS) {
			const limit = failureContext(error)?.max_purchased_boost_slots;
			this.boostLimit = typeof limit === 'number' ? limit : null;
			this.state = 'boost_limit';
			return;
		}
		if (code === APIErrorCodes.SHOP_RECURRING_NOT_ELIGIBLE) {
			this.state = 'recurring_unavailable';
			const body = error instanceof HttpError ? (error.body as {recurring_eligibility?: unknown} | null) : null;
			const eligibility = RecurringEligibility.safeParse(body?.recurring_eligibility);
			// Plus's eligibility; a boost refusal is about the boost subscription only.
			if (eligibility.success && this.kind === 'premium') ShopCatalog.setRecurringEligibility(eligibility.data);
			void ShopCatalog.load(true);
			return;
		}
		if (code === APIErrorCodes.COSMETIC_ALREADY_OWNED || code === APIErrorCodes.SHOP_USERNAME_ALREADY_OWNED) {
			this.state = 'already_owned';
			return;
		}
		this.state = 'busy';
		const rateLimited = error instanceof HttpError && error.status === 429;
		// The per-minute order bucket and the global limit ask for seconds, the daily one for up to 72 minutes.
		const waitSeconds = rateLimited ? (failureRetryAfter(error) ?? 0) : 0;
		this.waitMinutes = waitSeconds > 60 ? Math.ceil(waitSeconds / 60) : null;
		// Too many open orders, or a refusal nobody planned for: the server's localized message says why.
		const plainBusy = code === undefined || code === APIErrorCodes.SHOP_BUSY || rateLimited;
		this.failureMessage = plainBusy ? null : (failureMessage(error) ?? null);
	}
}

export function useShopCheckout(target: ShopCheckoutTarget): ShopCheckoutFlow {
	const {i18n} = useLingui();
	const [flow] = useState(
		() =>
			new ShopCheckoutFlow(target.kind, () => i18n.locale, target.kind === 'boost' ? target.here?.refresh : undefined),
	);
	const sku = shopCheckoutSku(target);
	const billingMode =
		(target.kind === 'premium' && target.billingMode) ||
		(target.kind === 'boost' && target.recurring ? 'recurring' : 'one_time');
	const quantity = (target.kind === 'boost' && target.recurring?.quantity) || null;
	const fixedName = (target.kind === 'username' && target.name) || null;
	const offer = target.kind === 'username' ? target.offer : undefined;
	const freeOnly = target.kind === 'username' && target.visionary === true;
	useEffect(() => {
		if (freeOnly) flow.freeOnly(sku);
		else
			void flow.quoteFor(
				sku,
				billingMode,
				quantity,
				fixedName === null ? null : {username: fixedName, ...(offer !== undefined && {offer})},
			);
	}, [flow, sku, billingMode, quantity, fixedName, offer, freeOnly]);
	useEffect(
		() => () => {
			// Closed while waiting: the server still delivers a payment that goes through.
			if (flow.state === 'awaiting') {
				ToastCommands.createToast({type: 'info', children: i18n._(PURCHASE_ADDED_ONCE_CONFIRMED_DESCRIPTOR)});
			}
			flow.dispose();
		},
		[flow],
	);
	return flow;
}
